import { useEffect, useMemo, useState } from 'react';

/**
 * O relógio da tela.
 *
 * A agenda desenha uma linha em cima do horário atual, e sem isto ela ficava
 * parada onde estava quando a tela montou: o barbeiro abria o app às 9h, deixava
 * aberto no balcão e às 11h a linha ainda dizia 9h — pior que não ter linha,
 * porque parece informação.
 *
 * Duas decisões:
 *
 *  - **Reage ao minuto, não ao segundo.** O estado é o minuto do relógio, então
 *    a tela redesenha uma vez por minuto e não trinta. É a mesma resolução do
 *    que aparece escrito (`HH:mm`).
 *  - **Acerta ao voltar para o app.** O celular congela o `setInterval` com a
 *    tela apagada; sem o `visibilitychange`, quem tira o telefone do bolso vê a
 *    linha atrasada até o próximo disparo.
 */
export function useNow(): Date {
  const [minute, setMinute] = useState(() => Math.floor(Date.now() / 60_000));

  useEffect(() => {
    const tick = () => setMinute(Math.floor(Date.now() / 60_000));

    // 30s e não 60s: o intervalo não se alinha à virada do minuto, e meio
    // minuto de erro numa linha que anda 2px por minuto ninguém vê.
    const timer = window.setInterval(tick, 30_000);
    const onVisible = () => {
      if (!document.hidden) tick();
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return useMemo(() => new Date(minute * 60_000), [minute]);
}
